import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { projectApi } from './services/api';
import TransactionList from './pages/TransactionList';
import TransactionDetail from './pages/TransactionDetail';
import Dashboard from './pages/Dashboard';
import ProjectManagement from './pages/ProjectManagement';
import OperationHistory from './pages/OperationHistory';
import HealthDashboard from './pages/HealthDashboard';

const ProjectContext = createContext(null);

export function useProject() {
    return useContext(ProjectContext);
}

function ProjectSelector() {
    const { projects, selectedProject, setSelectedProject } = useProject();

    return (
        <select
            value={selectedProject || ''}
            onChange={(e) => setSelectedProject(e.target.value || null)}
            className="bg-blue-700 text-white text-sm rounded px-2 py-1 border border-blue-600 focus:outline-none"
        >
            <option value="">All Projects</option>
            {projects.map((p) => (
                <option key={p.name} value={p.name}>{p.displayName || p.name}</option>
            ))}
        </select>
    );
}

function Navigation() {
    const location = useLocation();
    const isActive = (path) => location.pathname === path ? 'bg-blue-700' : '';

    return (
        <nav className="bg-blue-800 text-white shadow-lg">
            <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between h-16">
                    <div className="flex items-center space-x-8">
                        <Link to="/" className="text-xl font-bold tracking-tight">
                            CDC Audit Viewer
                        </Link>
                        <div className="flex space-x-4">
                            <Link to="/" className={`px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-700 ${isActive('/')}`}>
                                Dashboard
                            </Link>
                            <Link to="/transactions" className={`px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-700 ${isActive('/transactions')}`}>
                                Transactions
                            </Link>
                            <Link to="/projects" className={`px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-700 ${isActive('/projects')}`}>
                                Projects
                            </Link>
                            <Link to="/operations" className={`px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-700 ${isActive('/operations')}`}>
                                Operations
                            </Link>
                            <Link to="/health" className={`px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-700 ${isActive('/health')}`}>
                                Health
                            </Link>
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        <ProjectSelector />
                        <div className="text-sm text-blue-200">
                            Multi-Project CDC Audit
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
}

function App() {
    const [selectedProject, setSelectedProject] = useState(null);
    const [projects, setProjects] = useState([]);

    const refreshProjects = useCallback(async () => {
        try {
            const res = await projectApi.listProjects();
            setProjects(res.data || []);
        } catch (err) {
            console.error('Failed to load projects:', err);
        }
    }, []);

    useEffect(() => {
        refreshProjects();
        const interval = setInterval(refreshProjects, 30000);
        return () => clearInterval(interval);
    }, [refreshProjects]);

    const contextValue = {
        selectedProject,
        setSelectedProject,
        projects,
        refreshProjects,
    };

    return (
        <ProjectContext.Provider value={contextValue}>
            <Router>
                <div className="min-h-screen bg-gray-100">
                    <Navigation />
                    <main className="max-w-7xl mx-auto py-6 px-4">
                        <Routes>
                            <Route path="/" element={<Dashboard />} />
                            <Route path="/transactions" element={<TransactionList />} />
                            <Route path="/transactions/:transactionId" element={<TransactionDetail />} />
                            <Route path="/projects" element={<ProjectManagement />} />
                            <Route path="/operations" element={<OperationHistory />} />
                            <Route path="/health" element={<HealthDashboard />} />
                        </Routes>
                    </main>
                </div>
            </Router>
        </ProjectContext.Provider>
    );
}

export default App;
